import { Avatar, AvatarSize, LabelChipVariant, TableCell, theme, Chip, IconDictionary } from '@ketch-com/deck'
import { DataSystemDetails } from '@ketch-com/windlass/dist/dataSentry/v1/events/events'
import { Box, Typography, Tooltip } from '@mui/material'
import { GridColDef, GridColumnHeaderParams, GridRenderCellParams } from '@mui/x-data-grid-premium'
import { EmptyValueRenderer } from 'components/EmptyValueRenderer'
import { formatToK } from 'pages/dataSentry/utils/transformPieChartData'
import { AssetsTabTableLabelChip } from 'pages/dataSystems/DataSystem/assetsTab/components/AssetsTabTableLabelChip'
import {
  CalssificationExpansionDropdown,
  ClassificationIdString,
} from 'pages/dataSystems/DataSystem/assetsTab/components/ClassificationExpansionDropdown'
import { getSystemName } from 'pages/dataSentry/utils/getSystemName'
export interface DataSentrySystemsRowItem {
  id: DataSystemDetails['code']
  name: DataSystemDetails['name']
  logo: DataSystemDetails['logoUrl']
  trackers?: string[]
  trackerCount?: number
  collectionEvents: string
  dataCategories: string[]
  collectionSpectrum: string
  usheredBy?: DataSystemDetails[]
}

export const dataSentrySystemsDataGridUtil = (): GridColDef<DataSentrySystemsRowItem>[] => {
  const columns: GridColDef<DataSentrySystemsRowItem>[] = [
    {
      field: 'name',
      headerName: 'System',
      flex: 1.5,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        const logo = params.row.logo
        const hasLogo = !!logo?.trim()

        return (
          <Box display="flex" alignItems="center" gap={1.5} pl={1}>
            {hasLogo ? (
              <Avatar
                src={logo}
                alt={params.value}
                size={AvatarSize['large']}
                borderColor={theme.palette.Black.o8}
                isLogo
              />
            ) : (
              <Avatar
                icon="NoLogo"
                textColor={theme.palette.Text.Secondary}
                borderColor={theme.palette.Black.o8}
                size={AvatarSize['large']}
                isLogo
              />
            )}

            {params.row.name ? (
              <Typography variant="label">{params.row.name}</Typography>
            ) : (
              <Box display="flex" flexDirection="column">
                <Typography component="div" variant="label">
                  Unknown System
                </Typography>
                <Box display="flex" alignItems="center" gap={0.5}>
                  <Typography variant="footnote">{params.row.id}</Typography>
                </Box>
              </Box>
            )}
          </Box>
        )
      },
    },
    {
      align: 'left',
      field: 'usheredBy',
      headerName: 'Ushered By',
      flex: 0.75,
      headerAlign: 'left',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        const hasUsheredBy = (params?.row?.usheredBy?.length || 0) > 0
        const usheredBy = params?.row?.usheredBy
        return (
          <TableCell>
            {hasUsheredBy ? (
              <Box display="flex" gap={1} alignItems="center">
                <Tooltip
                  placement="bottom"
                  title={
                    <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                      {usheredBy?.map(system => <Typography key={system?.code}>{getSystemName(system)}</Typography>)}
                    </Box>
                  }
                >
                  <Box>
                    {usheredBy?.length === 1 ? (
                      <Avatar
                        src={usheredBy?.[0]?.logoUrl || ''}
                        icon={usheredBy?.[0]?.logoUrl ? undefined : IconDictionary.NoLogo}
                        alt={usheredBy?.[0]?.name || ''}
                        textColor={usheredBy?.[0]?.logoUrl ? theme.palette.Black.o48 : theme.palette.Text.Secondary}
                        size={AvatarSize['small']}
                        isLogo
                      />
                    ) : (
                      <Chip label={usheredBy?.length} size="small" />
                    )}
                  </Box>
                </Tooltip>
              </Box>
            ) : (
              'None'
            )}
          </TableCell>
        )
      },
    },
    {
      field: 'trackers',
      headerName: 'Trackers',
      align: 'right',
      headerAlign: 'center',
      flex: 0.5,
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: ({ row }: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        return (
          <Box>
            {
              <Tooltip
                placement="bottom"
                title={
                  row?.trackers?.length ? (
                    <Box display="flex" flexDirection="column" gap={0.5} maxHeight={120} overflow="auto">
                      {row?.trackers?.map((tracker, i) => <Typography key={tracker + i}>{tracker}</Typography>)}
                    </Box>
                  ) : (
                    ''
                  )
                }
              >
                <Box>
                  <Chip size="small" label={row?.trackerCount} />
                </Box>
              </Tooltip>
            }
          </Box>
        )
      },
    },
    {
      field: 'collectionEvents',
      headerName: 'Collection Events',
      flex: 0.75,
      align: 'right',
      headerAlign: 'right',
      sortable: false,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography
          variant="smallLabel"
          sx={{
            whiteSpace: 'normal',
            lineHeight: 1.2,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
          color={theme.palette.Text.Secondary}
          textAlign="right"
        >
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => (
        <Typography variant="body">{formatToK(params.value)}</Typography>
      ),
    },
    {
      field: 'dataCategories',
      headerName: 'Data Categories',
      sortable: false,
      flex: 2.5,
      renderHeader: (params: GridColumnHeaderParams<DataSentrySystemsRowItem>) => (
        <Typography variant="smallLabel" color={theme.palette.Text.Secondary}>
          {params.colDef.headerName}
        </Typography>
      ),
      renderCell: (params: GridRenderCellParams<DataSentrySystemsRowItem>) => {
        const categories: string[] = params.value || []

        const displayItems = categories.slice(0, 2)
        const dropDownListItems = categories.slice(2)

        return (
          <TableCell>
            {categories.length ? (
              <>
                {displayItems.map(category => (
                  <AssetsTabTableLabelChip
                    key={category}
                    title={category}
                    variant={LabelChipVariant.category}
                    hideConfidence
                    hideIcon
                    isViewOnly
                    supportMultiLineTitle
                  />
                ))}
                {dropDownListItems.length > 0 && (
                  <CalssificationExpansionDropdown
                    label={`+${dropDownListItems.length} more`}
                    items={dropDownListItems.map(name => ({
                      code: name,
                      name: name,
                    }))}
                    labelChipVariant={LabelChipVariant.category}
                    idKeyValue={ClassificationIdString.assetDataCategoryId}
                    isViewOnly
                    hideIcon
                    handleManualClassificationVerification={() => {}}
                  />
                )}
              </>
            ) : (
              <EmptyValueRenderer />
            )}
          </TableCell>
        )
      },
    },
  ]
  return columns
}
